import { useCallback, useEffect, useState } from 'react';

/**
 * ZoomableImage — a thumbnail that opens a full-screen, readable lightbox on
 * click. Concept diagrams carry real information; small on the page keeps the
 * layout clean, and the zoom lets students actually read them.
 *
 * Accessible: Escape + backdrop + ✕ all close it; body scroll locks while open;
 * the overlay is a labelled dialog. The alt text shows as a caption in the
 * zoomed view so the image's meaning is spelled out too.
 */
export function ZoomableImage({
  src,
  alt,
  imgClassName,
  wrapClassName,
}: {
  src: string;
  alt: string;
  imgClassName?: string;
  wrapClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // Cards fetch the tiny thumbnail (`*.webp`); the lightbox fetches the larger
  // `*.full.webp` only when the student actually zooms — so the page stays light.
  const zoomSrc = src.endsWith('.webp') ? src.replace(/\.webp$/, '.full.webp') : src;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Zoom in to read: ${alt}`}
        className={`group relative block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 rounded-md ${wrapClassName ?? ''}`}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          // Cached images can finish before React attaches onLoad — the ref
          // catches that case so a cached image never stays stuck invisible.
          ref={(n) => { if (n && n.complete && n.naturalWidth > 0 && !loaded) setLoaded(true); }}
          onLoad={() => setLoaded(true)}
          className={`${imgClassName ?? ''} transition-[opacity,transform] duration-500 ease-out-soft group-hover:scale-[1.015] motion-reduce:group-hover:scale-100 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
        {/* hover hint: magnifier + "Tap to read" */}
        <span
          aria-hidden="true"
          className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-ink-900/80 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-soft backdrop-blur-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 1 0 3.4 9.82l3.64 3.64a1 1 0 0 0 1.42-1.42l-3.64-3.64A5.5 5.5 0 0 0 9 3.5zM5.5 9a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0zM9 6.5a1 1 0 0 1 1 1V8h.5a1 1 0 1 1 0 2H10v.5a1 1 0 1 1-2 0V10h-.5a1 1 0 1 1 0-2H8v-.5a1 1 0 0 1 1-1z"
              clipRule="evenodd"
            />
          </svg>
          Tap to read
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={close}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-night-900/90 p-4 cursor-zoom-out backdrop-blur-sm"
        >
          {/* Click anywhere — including the image — zooms back out. */}
          <figure className="flex max-h-[94vh] max-w-[95vw] flex-col items-center">
            <img
              src={zoomSrc}
              alt={alt}
              className="max-h-[84vh] max-w-[95vw] cursor-zoom-out rounded-lg bg-white object-contain shadow-2xl"
            />
            <figcaption className="mt-3 max-w-[70ch] text-center text-sm leading-relaxed text-white/80">
              {alt}
            </figcaption>
          </figure>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white/90 ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20"
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}
